// Indent guides: how many levels deep each line is, so the renderer can tint one band per level.

export interface Indents {
  /** columns in a level when indented by spaces (a tab is always one level) */
  unit: number
  /** the depth of each line; a blank line takes the shallower of its neighbours so a guide runs through */
  levels: number[]
}

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a)

export const indentsOf = (lines: string[]): Indents => {
  let unit = 0
  const own: (number | null)[] = lines.map((l) => {
    if (!l.trim()) return null
    const ws = /^[ \t]*/.exec(l)![0]
    const tabs = (ws.match(/\t/g) ?? []).length
    const spaces = ws.length - tabs
    if (spaces) unit = gcd(unit, spaces)
    return tabs + spaces // resolved to levels below, once the unit is known
  })
  if (unit < 2) unit = 2
  if (unit > 4) unit = 4
  const raw = lines.map((l) => {
    const ws = /^[ \t]*/.exec(l)![0]
    const tabs = (ws.match(/\t/g) ?? []).length
    return tabs + Math.floor((ws.length - tabs) / unit)
  })
  const levels = own.map((o, i) => (o === null ? -1 : raw[i]!))
  // blank lines: min of the nearest non-blank above and below
  let above = 0
  const next: number[] = new Array(levels.length).fill(0)
  let below = 0
  for (let i = levels.length - 1; i >= 0; i--) {
    if (levels[i]! >= 0) below = levels[i]!
    next[i] = below
  }
  for (let i = 0; i < levels.length; i++) {
    if (levels[i]! >= 0) above = levels[i]!
    else levels[i] = Math.min(above, next[i]!)
  }
  return { unit, levels }
}

/** A line's leading whitespace cut into one piece per level (a tab, or `unit` spaces); the rest is returned apart. */
export const splitIndent = (ws: string, unit: number): { pieces: string[]; rest: string } => {
  const pieces: string[] = []
  let i = 0
  while (i < ws.length) {
    if (ws[i] === '\t') {
      pieces.push('\t')
      i++
    } else if (
      ws.length - i >= unit &&
      ws.slice(i, i + unit).trim() === '' &&
      !ws.slice(i, i + unit).includes('\t')
    ) {
      pieces.push(ws.slice(i, i + unit))
      i += unit
    } else break
  }
  return { pieces, rest: ws.slice(i) }
}
